/**
 * RecordPaymentDialog — record what actually left the bank against one invoice.
 * One click pays the full remaining balance; otherwise type a partial amount.
 */
import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Banknote, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { API } from "@/App";

const money = (v) => `$${(Number(v) || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

export default function RecordPaymentDialog({ invoice, token, onClose, onSaved }) {
  const headers = { Authorization: `Bearer ${token}` };
  const [methods, setMethods] = useState([]);
  const [banks, setBanks] = useState([]);
  const [busy, setBusy] = useState(false);
  const [form, setForm] = useState({
    method: "wire", amount: "", payment_date: new Date().toISOString().slice(0, 10),
    reference: "", bank_account_id: "", notes: "",
  });

  useEffect(() => {
    (async () => {
      try {
        const r = await axios.get(`${API}/payment-runs/settings`, { headers });
        setMethods(r.data.methods || []);
        const accounts = r.data.settings?.bank_accounts || [];
        setBanks(accounts);
        const def = accounts.find((a) => a.default) || accounts[0];
        if (def) setForm((f) => ({ ...f, bank_account_id: def.id }));
      } catch { /* quiet */ }
    })();
  }, [token]); // eslint-disable-line react-hooks/exhaustive-deps

  if (!invoice) return null;
  const balance = Number(invoice.balance) || 0;

  const save = async () => {
    setBusy(true);
    try {
      const r = await axios.post(`${API}/payment-runs/record-payment`, {
        invoice_id: invoice.id,
        method: form.method,
        amount: form.amount === "" ? null : Number(form.amount),
        payment_date: form.payment_date,
        reference: form.reference,
        bank_account_id: form.bank_account_id || null,
        notes: form.notes,
      }, { headers });
      toast.success(r.data.paid_in_full
        ? `Invoice ${invoice.invoice_number} paid in full`
        : `${money(r.data.payment.amount)} recorded — ${money(r.data.remaining)} still owed`);
      onSaved?.(r.data);
      onClose?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not record that payment");
    } finally { setBusy(false); }
  };

  return (
    <Dialog open={!!invoice} onOpenChange={(v) => !v && onClose?.()}>
      <DialogContent className="max-w-md" data-testid="record-payment-dialog">
        <DialogHeader>
          <DialogTitle>Record a payment</DialogTitle>
          <DialogDescription>
            Log what actually left the bank against this invoice.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-2.5">
          <div className="rounded-lg border border-slate-200 bg-slate-50 p-2.5 text-xs">
            <p className="font-bold text-slate-900">{invoice.supplier_name}</p>
            <p className="font-mono text-slate-600">{invoice.invoice_number}</p>
            <p className="text-slate-600 mt-1">
              Billed {money(invoice.amount)} · <strong>{money(balance)} still owed</strong>
            </p>
          </div>

          <div>
            <label className="text-[10px] font-black uppercase tracking-wider text-slate-500">Method</label>
            <select value={form.method} data-testid="payment-method"
              className="h-9 w-full text-sm border border-slate-300 rounded-md px-2"
              onChange={(e) => setForm((f) => ({ ...f, method: e.target.value }))}>
              {methods.map((m) => <option key={m.key} value={m.key}>{m.label}</option>)}
            </select>
          </div>

          <div>
            <label className="text-[10px] font-black uppercase tracking-wider text-slate-500">Amount</label>
            <div className="flex gap-2">
              <Input type="number" step="0.01" value={form.amount} data-testid="payment-amount"
                placeholder={`${balance.toFixed(2)} (full balance)`}
                onChange={(e) => setForm((f) => ({ ...f, amount: e.target.value }))} />
              <Button size="sm" variant="outline" className="whitespace-nowrap"
                onClick={() => setForm((f) => ({ ...f, amount: balance.toFixed(2) }))}
                data-testid="payment-full-balance">
                Pay full balance
              </Button>
            </div>
            <p className="text-[10.5px] text-slate-400 mt-0.5">
              Leave it blank to pay the full {money(balance)}, or type a partial amount.
            </p>
          </div>

          <div className="grid grid-cols-2 gap-2">
            <div>
              <label className="text-[10px] font-black uppercase tracking-wider text-slate-500">Payment date</label>
              <Input type="date" value={form.payment_date} data-testid="payment-date"
                onChange={(e) => setForm((f) => ({ ...f, payment_date: e.target.value }))} />
            </div>
            <div>
              <label className="text-[10px] font-black uppercase tracking-wider text-slate-500">Confirmation #</label>
              <Input value={form.reference} placeholder="20261007-00005015" data-testid="payment-reference"
                onChange={(e) => setForm((f) => ({ ...f, reference: e.target.value }))} />
            </div>
          </div>

          <div>
            <label className="text-[10px] font-black uppercase tracking-wider text-slate-500">Paid from</label>
            <select value={form.bank_account_id} data-testid="payment-bank"
              className="h-9 w-full text-sm border border-slate-300 rounded-md px-2"
              onChange={(e) => setForm((f) => ({ ...f, bank_account_id: e.target.value }))}>
              <option value="">— not recorded —</option>
              {banks.map((b) => (
                <option key={b.id} value={b.id}>
                  {b.institution}{b.nickname ? ` · ${b.nickname}` : ""}{b.last4 ? ` ••••${b.last4}` : ""}
                </option>
              ))}
            </select>
            {!banks.length && (
              <p className="text-[10.5px] text-amber-700 mt-0.5">
                No bank accounts saved yet — add one in Payments &amp; runs → Settings so the
                supplier letter can name the institution.
              </p>
            )}
          </div>

          <Textarea rows={2} value={form.notes} placeholder="Notes (optional)" data-testid="payment-notes"
            onChange={(e) => setForm((f) => ({ ...f, notes: e.target.value }))} />

          <div className="flex justify-end gap-2">
            <Button size="sm" variant="outline" onClick={onClose}>Cancel</Button>
            <Button size="sm" className="bg-emerald-600 hover:bg-emerald-700" onClick={save} disabled={busy}
              data-testid="payment-save">
              {busy ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> : <Banknote className="w-3.5 h-3.5 mr-1" />}
              Record payment
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
